// Module depenencies
import { XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import validator from 'validator';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useChangePasswordMutation } from 'features/auth/authApi';

// UI
import { Dialog, DialogActions, DialogBody, DialogTitle } from 'components/catalyst/dialog'
import { Button } from 'components/catalyst/button';
import { ErrorMessage, Field, FieldGroup, Fieldset, Label } from 'components/catalyst/fieldset';
import { Input } from 'components/catalyst/input';
import LoadingButton from 'components/common/LoadingButton';

const ChangePasswordModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation('common');
  const [changePassword, { isLoading }] = useChangePasswordMutation();

  const { register, handleSubmit, formState: { errors, isValid }, getValues, reset } = useForm({
    mode: 'onBlur',
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data) => {
    if (!isValid) {
      return
    }
    
    const { currentPassword, newPassword } = data;

    try {
      await changePassword({ currentPassword, newPassword }).unwrap();

      toast.success(t('PASSWORD_CHANGE_SUCCESS'));

      handleClose();
    } catch (error) {
      // Skip showing error toast if it's a 403 permission error (already handled globally)
      if (error?.status !== 403) {
        toast.error(error.response?.data?.message || t('ERROR_MESSAGE'));
      }
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
    >
      <DialogTitle>
        <div className='flex items-center justify-between'>
          {t('PASSWORD_CHANGE_TITLE')}

          <Button
            plain
            onClick={handleClose}
          >
            <XMarkIcon />
          </Button>
        </div>
      </DialogTitle>

      <DialogBody>
        <form>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>{t('PASSWORD_CHANGE_LABEL_CURRENT_PASSWORD')}</Label>

                <Input
                  type="password"
                  name="currentPassword"
                  {...register('currentPassword', {
                    required: t('ERROR_REQUIRED_FIELD'),
                  })}
                  placeholder={t('PASSWORD_CHANGE_PLACEHOLDER_CURRENT_PASSWORD')}
                />

                {errors.currentPassword && (
                  <ErrorMessage>{errors.currentPassword.message}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>{t('PASSWORD_CHANGE_LABEL_NEW_PASSWORD')}</Label>

                <Input
                  type="password"
                  name="newPassword"
                  {...register('newPassword', {
                    required: t('ERROR_REQUIRED_FIELD'),
                    validate: (value) =>
                      validator.isLength(value, { min: 8 }) || t('ERROR_INVALID_PASS_LENGTH'),
                  })}
                  placeholder={t('PASSWORD_CHANGE_PLACEHOLDER_NEW_PASSWORD')}
                />

                {errors.newPassword && (
                  <ErrorMessage>{errors.newPassword.message}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>{t('PASSWORD_CHANGE_LABEL_CONFIRM_PASSWORD')}</Label>

                <Input
                  type="password"
                  name="confirmPassword"
                  {...register('confirmPassword', {
                    required: t('ERROR_REQUIRED_FIELD'),
                    validate: (value) =>
                      value === getValues('newPassword') || t('PASSWORD_CHANGE_ERROR_MISMATCH'),
                  })}
                  placeholder={t('PASSWORD_CHANGE_PLACEHOLDER_CONFIRM_PASSWORD')}
                />

                {errors.confirmPassword && (
                  <ErrorMessage>{errors.confirmPassword.message}</ErrorMessage>
                )}
              </Field>
            </FieldGroup>
          </Fieldset>
        </form>
      </DialogBody>

      <DialogActions>
        <Button outline onClick={handleClose}>
          {t('GLOBAL_CANCEL')}
        </Button>

        <LoadingButton
          disabled={isLoading || !isValid}
          onClick={handleSubmit(onSubmit)}
          color='blue'
          isLoading={isLoading}
          label={t('PASSWORD_CHANGE_BUTTON')}
          loadingLabel={t('PASSWORD_CHANGE_BUTTON_LOADING')}
        />
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordModal;
